import React, { useEffect, useState } from 'react';
import { Button } from '@clearcover/atlas';
import { useAuth0 } from 'Auth';
import { Modal } from 'Shared/components/Modal';
import { deactivateBlockListAttribute } from 'Shared/services/Reznor/DeactivateBlockListAttribute';
import { extractReznorError } from 'Shared/services/Reznor/extractReznorError';
import { ATTRIBUTE_TYPES } from 'BlockList/constants';
import { formatAttributeValueText } from 'BlockList/utils/formatAttributeValueText';
import type { BlockListRow } from 'BlockList/types';
import styles from './useConfirmDeactivate.module.css';

export type UseConfirmDeactivateOptions = {
  onSuccess: () => void;
};

export type UseConfirmDeactivateResult = {
  openDeactivate: (row: BlockListRow) => void;
  modal: React.ReactNode;
};

export const useConfirmDeactivate = (
  options: UseConfirmDeactivateOptions
): UseConfirmDeactivateResult => {
  const { onSuccess } = options;
  const { getToken } = useAuth0();

  const [pendingRow, setPendingRow] = useState<BlockListRow | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const openDeactivate = (row: BlockListRow) => {
    setPendingRow(row);
    setErrorMessage(null);
  };

  const cancel = () => {
    if (isSubmitting) return;
    setPendingRow(null);
    setErrorMessage(null);
  };

  // Close on Esc when the modal is open. Don't cancel mid-submit.
  useEffect(() => {
    if (pendingRow == null) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) cancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRow, isSubmitting]);

  const confirm = async () => {
    if (!pendingRow) return;
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const token = await getToken();
      await deactivateBlockListAttribute(token, pendingRow.id);
      setPendingRow(null);
      onSuccess();
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { status?: number; data?: unknown };
        message?: string;
      };
      const status = axiosErr.response?.status;
      const detail = extractReznorError(axiosErr.response?.data);

      if (status === 422 || status === 404) {
        setErrorMessage(detail ?? 'This attribute could not be deactivated.');
      } else if (status === 403) {
        setErrorMessage(
          detail ?? 'You do not have permission to deactivate this attribute.'
        );
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const attrTypeLabel = pendingRow
    ? ATTRIBUTE_TYPES[pendingRow.attributeType].label
    : '';
  const attrValueDisplay = pendingRow
    ? formatAttributeValueText(pendingRow.attributeType, pendingRow.value)
    : '';

  const modal = (
    <Modal showModal={pendingRow != null} onClose={cancel}>
      <div className={styles.container}>
        <div className={styles.body}>
          <h2 className={styles.title}>Deactivate this attribute?</h2>
          <p className={styles.message}>
            New applications will no longer be matched against this attribute.
          </p>
          <p className={styles.warning}>
            This cannot be undone — once deactivated, the attribute cannot be
            re-activated.
          </p>
          <div
            className={styles.attributeBox}
            data-testid="deactivate-attribute-box"
          >
            <div className={styles.attributeLabel}>{attrTypeLabel}</div>
            <div className={styles.attributeValue}>{attrValueDisplay}</div>
          </div>
          {errorMessage && <p className={styles.error}>{errorMessage}</p>}
        </div>
        <div className={styles.footer}>
          <Button
            variant="secondary"
            onClick={cancel}
            disabled={isSubmitting}
            data-testid="go-back-button"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirm}
            isSubmitting={isSubmitting}
            data-testid="confirm-button"
          >
            Deactivate
          </Button>
        </div>
      </div>
    </Modal>
  );

  return { openDeactivate, modal };
};
